import { QueryKeys } from './services.types';

/**
 * This function is used to generate query keys for useQuery based on the path provided.
 * The path is split by '/' and the resulting array is used as the main key.
 * So for example, if the path is '/projects/1/microgrids/1', the main key will be ['projects', '1', 'microgrids', '1'].
 * The list and detail keys are generated by appending 'list' and 'detail' to the main key.
 * @param {string} path
 * @returns object with mainKey, list and detail keys
 */
export const getQueryKeys = (path: string): QueryKeys => {
  const mainKey = path.split('/').filter((item) => item);
  return {
    mainKey,
    list: (filters) => [...mainKey, 'list', { filters }],
    detail: [...mainKey, 'detail'],
  };
};

export const ACKNOWLEDGEMENTS_CACHE_KEY = (assetId: number | string) => ['acknowledgements', `${assetId}`];

export const ALERT_COUNTS_CACHE_KEY = ({
  projectFilter = '',
  assetFilter = '',
}: { projectFilter?: string; assetFilter?: string } = {}) => ['alert-counts', projectFilter, assetFilter];

export const ALERT_EMAIL_RECIPIENTS_CACHE_KEY = (organisationId: string | number = '') => [
  'alert-emails',
  `${organisationId}`,
];

export const ALERT_EVENT_CONFIGS_CACHE_KEY = ['alert-event-configs'];

export const ALERT_SETTINGS_ORGANISATION_CACHE_KEY = (organisationId: string | number = '') => [
  'alert-settings-organisation',
  `${organisationId}`,
];

export const ALERT_SETTINGS_ASSET_CACHE_KEY = (organisationId: string | number = '', assetId: string | number = '') => [
  'alert-settings-asset',
  `${organisationId}`,
  `${assetId}`,
];

export const ALERT_ROBOCALL_RECIPIENTS_CACHE_KEY = (organisationId: string | number = '') => [
  'alert-robocalls',
  `${organisationId}`,
];

export const ASSET_CACHE_KEY = (assetId: string | number = '') => ['asset', `${assetId}`];

export const ASSET_REPORTS_CACHE_KEY = (assetId: string | number = '') => ['asset-reports', `${assetId}`];

export const ASSETS_CACHE_KEY = ({
  searchTerm = '',
  organisationId = '',
  deviceTypeKey = 'all',
  deviceModelKey = '',
}: {
  searchTerm?: string;
  organisationId?: string | number;
  deviceTypeKey?: string;
  deviceModelKey?: string;
} = {}) => ['assets', searchTerm, `${organisationId}`, deviceTypeKey, deviceModelKey];

export const BLUEPRINTS_BASE_KEY = ['blueprints'] as const;

export const BLUEPRINT_DETAILS_CACHE_KEY = (blueprintId: string | number) => ['blueprint-details', `${blueprintId}`];

export const BLUEPRINT_POWER_PROFILE_CACHE_KEY = (blueprintId: string | number) => [
  'blueprint-power-profile',
  `${blueprintId}`,
];

export const BLUEPRINT_SHARED_TOKEN_CACHE_KEY = (blueprintId: string | number) => [
  'blueprint-shared-token',
  `${blueprintId}`,
];

export const COUNTRIES_CACHE_KEY = ['countries'];

export const DEVICE_MODEL_CACHE_KEY = (deviceModelId: string | number = '') => ['deviceModel', `${deviceModelId}`];

export const DEVICE_MODELS_CACHE_KEY = ['device-models'];

export const DEVICE_TYPES_CACHE_KEY = ['device-types'];

export const EQUIPMENT_CACHE_KEY = (organisationId: string | number = '') => ['equipment', 'list', `${organisationId}`];

export const EQUIPMENT_DETAIL_CACHE_KEY = (equipmentId: string | number = '') => [
  'equipment',
  'detail',
  `${equipmentId}`,
];

export const EQUIPMENT_RECOMMENDATION_CACHE_KEY = ({
  mode = '',
  gridSizeAmperes = 0,
  gridSizeKw = 0,
  kitePowerScenario = '',
  powerProfileData = '',
} = {}) => [
  'equipment-recommendations',
  mode,
  `${gridSizeAmperes}`,
  `${gridSizeKw}`,
  kitePowerScenario,
  `${powerProfileData}`,
];

export const EVENTS_CACHE_KEY = ({
  unitId,
  perPage = 50,
  severityFilters = [],
  filters = [],
}: {
  unitId: string;
  perPage?: number;
  severityFilters?: string[];
  filters?: { filterKey: string; value: string | number | Array<string | number> }[];
}) => [
  'events-v2',
  unitId,
  perPage.toString(),
  ...severityFilters,
  ...filters.map((f) => (Array.isArray(f.value) ? `${f.filterKey}:${f.value.join(',')}` : `${f.filterKey}:${f.value}`)),
];

export const FAVOURITE_PROJECTS_CACHE_KEY = ['all-projects', 'favourites'];

export const FLEET_CACHE_KEY = (fleetId: string | number = '') => ['fleet', `${fleetId}`];

export const FLEETS_CACHE_KEY = ['fleets'];

export const MULTI_TIMESERIES_CACHE_KEY = ({
  unitExtIds,
  fields,
  from,
  to,
  groupTime,
  dataTable,
}: {
  unitExtIds: string;
  fields: string;
  from: number | null;
  to: number | null;
  groupTime?: string;
  dataTable?: string;
}) => ['multiTimeseries', unitExtIds, fields, `${from}_${to}`, groupTime, dataTable];

export const ORGANISATION_CACHE_KEY = (organisationId: string | number = '') => ['organisation', `${organisationId}`];

export const ORGANISATIONS_CACHE_KEY = (
  perPage: number = 12,
  searchTerm: string = '',
  filters: { key: string; value: string | number | boolean }[] = []
) => ['organisations', perPage.toString(), searchTerm, JSON.stringify(filters)];

export const PROJECT_CACHE_KEY = (externalId: string | number = '') => ['project', `${externalId}`];

export const PROJECT_REPORTS_CACHE_KEY = (projectId: string | number = '') => ['project-reports', `${projectId}`];

export const PROJECTS_CACHE_KEY = ({
  organisationId = '',
  searchTerm = '',
  order = '',
  orderBy = '',
}: { organisationId?: string | number; searchTerm?: string; order?: string; orderBy?: string } = {}) => [
  'all-projects',
  `${organisationId}`,
  searchTerm,
  orderBy,
  order,
];
export const PROJECT_TYPES_CACHE_KEY = ['project-types'];

export const RECENT_ASSETS_CACHE_KEY = ['assets', 'recent'];

export const RENTAL_BATTERY_DETAIL_CACHE_KEY = (batteryId: string | number = '') => [
  'equipment',
  'rental-battery-detail',
  `${batteryId}`,
];
export const RENTAL_BATTERIES_CACHE_KEY = ['equipment', 'rental-batteries'];

export const USER_CACHE_KEY = (userId: string | number = '') => ['user', `${userId}`];

export const USER_ME_CACHE_KEY = ['users/me'];

export const USER_ROLES_CACHE_KEY = ['user-roles'];

export const USERS_CACHE_KEY = (
  page: string = '',
  perPage: string = '',
  organisationId: string | number = '',
  searchTerm: string = '',
  order: string = ''
) => ['users', page, perPage, `${organisationId}`, searchTerm, order];

export const WATCHER_TYPE_CACHE_KEY = (watcherTypeId: number | string) => [`${watcherTypeId}`, 'watcher-type'];

export const WATCHER_TYPES_CACHE_KEY = (perPage = 10, searchTerm = '', order = 'asc') => [
  'watcher-types',
  `${perPage}`,
  `${searchTerm}`,
  `${order}`,
];
